import { jsx } from 'react/jsx-runtime';
import { useState, useRef, useContext, useEffect } from 'react';
import { ReactReduxContext } from 'react-redux';
import { Router } from 'react-router';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/**
 * This action type will be dispatched when your history
 * receives a location change.
 */
const ROUTER_ON_LOCATION_CHANGED = '@@router/ON_LOCATION_CHANGED';
const onLocationChanged = (location, action) => ({
    type: ROUTER_ON_LOCATION_CHANGED,
    payload: { location, action },
});
/**
 * This action type will be dispatched by the history actions below.
 * If you're writing a middleware to watch for navigation events, be sure to
 * look for actions of this type.
 */
const ROUTER_CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';
function updateLocation(method) {
    return (...args) => ({
        type: ROUTER_CALL_HISTORY_METHOD,
        payload: { method: method, args },
    });
}
/**
 * These actions correspond to the history API.
 * The associated routerMiddleware will capture these events before they get to
 * your reducer and reissue them as the matching function on your history.
 */
/**
 * Pushes a new location onto the history stack, increasing its length by one.
 * If there were any entries in the stack after the current one, they are
 * lost.
 *
 * @param to - The new URL
 * @param state - Data to associate with the new location
 *
 * @see https://github.com/remix-run/history/tree/main/docs/api-reference.md#history.push
 */
const push = updateLocation('push');
/**
 * Replaces the current location in the history stack with a new one.  The
 * location that was replaced will no longer be available.
 *
 * @param to - The new URL
 * @param state - Data to associate with the new location
 *
 * @see https://github.com/remix-run/history/tree/main/docs/api-reference.md#history.replace
 */
const replace = updateLocation('replace');
/**
 * Navigates `n` entries backward/forward in the history stack relative to the
 * current index. For example, a "back" navigation would use go(-1).
 *
 * @param delta - The delta in the stack index
 *
 * @see https://github.com/remix-run/history/tree/main/docs/api-reference.md#history.go
 */
const go = updateLocation('go');
/**
 * Navigates to the next entry in the stack. Identical to go(1).
 *
 * @see https://github.com/remix-run/history/tree/main/docs/api-reference.md#history.forward
 */
const back = updateLocation('back');
/**
 * Sets up a listener that will be called whenever the current location
 * changes.
 *
 * @param listener - A function that will be called when the location changes
 * @returns unlisten - A function that may be used to stop listening
 *
 * @see https://github.com/remix-run/history/tree/main/docs/api-reference.md#history.listen
 */
const forward = updateLocation('forward');
const routerActions = {
    push,
    replace,
    go,
    back,
    forward,
};
// Middleware
function createRouterMiddleware(history) {
    return () => next => (action) => {
        if (action.type !== ROUTER_CALL_HISTORY_METHOD) {
            return next(action);
        }
        history[action.payload.method](...action.payload.args);
    };
}
function createRouterReducer(history) {
    const initialRouterState = {
        location: history.location,
        action: history.action,
    };
    /*
    * This reducer will update the state with the most recent location history
    * has transitioned to.
    */
    return (state = initialRouterState, action) => {
        if (action.type === ROUTER_ON_LOCATION_CHANGED) {
            return Object.assign(Object.assign({}, state), action.payload);
        }
        return state;
    };
}
function reduxRouterSelector(state) {
    return state.router;
}
const development = process.env.NODE_ENV === 'development';
function ReduxRouter(_a) {
    var { enableTimeTravelling = development, routerSelector = reduxRouterSelector } = _a, props = __rest(_a, ["enableTimeTravelling", "routerSelector"]);
    const [state, setState] = useState({
        action: props.history.action,
        location: props.history.location,
    });
    const timeTravellingRef = useRef(false);
    let store;
    if (props.store !== undefined) {
        store = props.store;
    }
    else {
        try {
            const reactReduxContextValue = useContext(ReactReduxContext);
            store = reactReduxContextValue.store;
        }
        catch (e) {
            console.error('Please pass the "store" property');
        }
    }
    useEffect(() => {
        let removeStoreSubscription;
        let removeHistoryListener;
        if (enableTimeTravelling === true) {
            removeStoreSubscription = store.subscribe(() => {
                // Extract store's location and browser location
                const locationInStore = routerSelector(store.getState()).location;
                const historyLocation = props.history.location;
                // If we do time travelling, the location in store is changed but location in history is not changed
                if (props.history.action === 'PUSH' &&
                    (historyLocation.pathname !== locationInStore.pathname ||
                        historyLocation.search !== locationInStore.search ||
                        historyLocation.hash !== locationInStore.hash ||
                        historyLocation.state !== locationInStore.state)) {
                    timeTravellingRef.current = true;
                    props.history.push(locationInStore);
                }
            });
        }
        removeHistoryListener = props.history.listen(({ location, action }) => {
            if (timeTravellingRef.current === false) {
                store.dispatch(onLocationChanged(location, action));
            }
            else {
                timeTravellingRef.current = false;
            }
            setState({ action, location });
        });
        return function cleanup() {
            removeHistoryListener();
            if (removeStoreSubscription !== undefined) {
                removeStoreSubscription();
            }
        };
    }, development ? [enableTimeTravelling, history, routerSelector] : []);
    return (jsx(Router, { navigationType: state.action, location: state.location, basename: props.basename, navigator: props.history, children: props.children }));
}

export { ROUTER_CALL_HISTORY_METHOD, ROUTER_ON_LOCATION_CHANGED, ReduxRouter, back, createRouterMiddleware, createRouterReducer, forward, go, onLocationChanged, push, reduxRouterSelector, replace, routerActions };
//# sourceMappingURL=index.es.js.map
